import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useCreditApplications from "hooks/donations/useCreditApplications";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React from "react";
import { Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

export const CreditApplications = () => {
  const navigate = useNavigate();
  const { creditApplications, loading, error, usersLoading } = useCreditApplications();

  const { formatDateTime } = useFormattedDate();
  const formatID = (id) => `${id.slice(0, 12)}...`;
  const formatText = (text) => `${text.slice(0, 20)}...`;

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => formatID(getValue()),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "applicant",
      header: "Applicant",
      accessorFn: (row) => {
        const donor = row.applicant;
        if (!donor) return "NULL";

        // Combine first + last name, or fallback to nickname
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      cell: ({ row }) => {
        const donor = row.original.applicant;
        if (!donor) return "NULL";
        const fullName = `${donor.firstName || ""} ${
          donor.lastName || ""
        }`.trim();
        return fullName || donor.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date applied",
      cell: ({ getValue }) => {
        return formatDateTime(getValue());
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const statusMap = {
          CREDIT_ISSUED: { type: "info", label: "Credit Issued" },
          PENDING: { type: "warning", label: "Pending" },
          APPROVED: { type: "success", label: "Approved" },
          DELETED: { type: "danger", label: "Deleted" },
        };

        const { type, label } = statusMap[value] || {
          type: "secondary",
          label: "Unknown",
        };
        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "documents",
      header: "Docs",
      cell: ({ getValue }) => {
        const docs = getValue();
        const type = docs.length > 0 ? "success" : "secondary";
        const label = docs.length > 0 ? "Yes" : "No";

        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "references",
      header: "Refs",
      cell: ({ getValue }) => {
        const refs = getValue();
        const type = refs.length > 0 ? "success" : "secondary";
        const label = refs.length > 0 ? "Yes" : "No";

        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2dd">
              <Dropdown.Item
                onClick={() =>
                  navigate(`/olitask/donations/credit-applications/${item.id}`)
                }
              >
                View
              </Dropdown.Item>
              {item.status !== "PENDING" && (
                <Guard privilege={PRIV.ISSUE_CREDIT}>
                  <Dropdown.Item
                    onClick={() =>
                      navigate(
                        `/olitask/donations/credit-application/${item.id}/issue-credit`
                      )
                    }
                  >
                    Issue credit
                  </Dropdown.Item>
                </Guard>
              )}

              <Guard privilege={PRIV.DELETE_APPLICATIONS}>
                <Dropdown.Divider as="div" className="my-0" />
                <Dropdown.Item onClick={() => {}} className="text-danger">
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: creditApplications,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  if (loading || usersLoading || creditApplications.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="hands-helping"
        title="Credit applications"
        subtitle="Manage and monitor all credit applications"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <AdvanceTableProvider {...table}>
        <Row className="mb-2">
          <Col md={4}>
            <AdvanceTableSearchBox />
          </Col>
        </Row>

        <AdvanceTable
          headerClassName="bg-200 text-nowrap align-middle"
          rowClassName="btn-reveal-trigger text-nowrap align-middle"
          tableProps={{
            bordered: true,
            striped: true,
            className: "fs-10 mb-0 overflow-hidden",
          }}
        />

        <div className="mt-3">
          <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
        </div>
      </AdvanceTableProvider>
    </>
  );
};
